import type { Bridge } from '@casa/bridge';

export interface EchoTestOptions {
    host: string;
    port: number;
    /** Number of random bytes to send and expect back. */
    bytes: number;
    timeoutMs?: number;
}

export interface EchoTestResult {
    ok: boolean;
    bytes: number;
    received: number;
    /** Time until the connection was open. */
    openMs: number;
    /** Time from the first byte sent to the last byte echoed. */
    roundTripMs: number;
    /** Payload rate through the bridge, counting both directions. */
    mbitPerSecond: number;
    error?: string;
}

const RANDOM_BLOCK = 65_536; // getRandomValues limit

function randomBytes(length: number) : Uint8Array {
    const bytes = new Uint8Array(length);
    for (let offset = 0; offset < length; offset += RANDOM_BLOCK) {
        crypto.getRandomValues(bytes.subarray(offset, offset + RANDOM_BLOCK));
    }
    return bytes;
}

/**
 * Iteration I1 exit check: random bytes sent to a TCP echo server (scripts/tcp-echo.mjs) through
 * `net.tcp` must come back unchanged.
 */
export async function runEchoTest(bridge: Bridge, options: EchoTestOptions) : Promise<EchoTestResult> {
    const sent = randomBytes(options.bytes);
    const echoed = new Uint8Array(sent.length);
    const result: EchoTestResult = {
        ok: false,
        bytes: sent.length,
        received: 0,
        openMs: 0,
        roundTripMs: 0,
        mbitPerSecond: 0,
    };

    const started = performance.now();
    let channel;
    try {
        channel = await bridge.net.tcp.open(options.host, options.port);
    } catch (error) {
        return { ...result, error: `open failed: ${error instanceof Error ? error.message : String(error)}` };
    }
    result.openMs = performance.now() - started;

    const sendStarted = performance.now();
    try {
        await new Promise<void>((resolve, reject) => {
            const timer = setTimeout(
                () => reject(new Error(`timed out after ${result.received} of ${sent.length} bytes`)),
                options.timeoutMs ?? 30_000,
            );
            const finish = (error?: Error) => {
                clearTimeout(timer);
                if (error) {
                    reject(error);
                } else {
                    resolve();
                }
            };
            channel.onData((chunk) => {
                if (result.received + chunk.length > echoed.length) {
                    finish(new Error('received more bytes than were sent'));

                    return;
                }
                echoed.set(chunk, result.received);
                result.received += chunk.length;
                if (result.received === echoed.length) {
                    finish();
                }
            });
            channel.onClose((reason) => finish(new Error(`closed early: ${reason ?? 'by peer'}`)));
            const sendAll = async () : Promise<void> => {
                try {
                    await channel.send(sent);
                } catch (error) {
                    finish(error instanceof Error ? error : new Error(String(error)));
                }
            };
            void sendAll();
            if (sent.length === 0) {
                finish();
            }
        });
        result.roundTripMs = performance.now() - sendStarted;

        const mismatch = echoed.findIndex((byte, i) => byte !== sent[i]);
        if (mismatch >= 0) {
            result.error = `byte ${mismatch} differs`;
        } else {
            result.ok = true;
        }
        if (result.roundTripMs > 0) {
            result.mbitPerSecond = (sent.length * 2 * 8) / 1e6 / (result.roundTripMs / 1000);
        }
    } catch (error) {
        result.error = error instanceof Error ? error.message : String(error);
    } finally {
        try {
            await channel.close();
        } catch {
            // A connection that the peer already closed needs no closing.
        }
    }
    return result;
}
